import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type SecuritySettingRowProps = {
  icon: LucideIcon;
  title: string;
  detail: string;
  status: string;
  statusVariant?: "active" | "neutral" | "highlight";
  trailing?: ReactNode;
  className?: string;
};

const statusClasses = {
  active: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  neutral: "bg-zinc-100 text-zinc-500 dark:bg-white/[0.06] dark:text-white/45",
  highlight: "bg-brand/15 text-brand",
};

export const SecuritySettingRow = ({
  icon: Icon,
  title,
  detail,
  status,
  statusVariant = "active",
  trailing,
  className,
}: SecuritySettingRowProps) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-xl border border-zinc-200/50 bg-white p-2.5 shadow-sm dark:border-white/[0.05] dark:bg-[#14161c] sm:gap-3.5 sm:p-3",
      className,
    )}
  >
    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-white/[0.06] sm:size-10">
      <Icon
        className="size-3.5 text-zinc-600 dark:text-brand sm:size-4"
        aria-hidden
      />
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-zinc-800 dark:text-white/90 sm:text-[13px]">
          {title}
        </p>
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-medium sm:text-[11px]",
            statusClasses[statusVariant],
          )}
        >
          {status}
        </span>
      </div>
      <p className="text-[11px] leading-snug text-zinc-500 dark:text-white/50">
        {detail}
      </p>
    </div>
    {trailing}
  </div>
);
